import React, { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from 'components/Button';
import { useStore } from 'context/store-context';
import {
  GET_DASHBOARD_ANALYTICS,
  GET_STORE_ANALYTICS,
  GET_STORE_ANALYTICS_BY_CHANNEL,
} from 'gql/queries';
import { getStoreCurrencyFormatter } from 'utils';
import { getDashboardMetricSummary } from './data/dashboard-data';

interface ProtectionDates {
  startDate: string;
  endDate: string;
}

interface DatedValue {
  createdAt: string;
  protectionTotal?: number | string;
  total?: number | string;
}

interface OrderAnalytics {
  totalOrders: { aggregate: { total: number }; nodes: DatedValue[] };
  protectedOrders: { aggregate: { total: number }; nodes: DatedValue[] };
  unprotectedOrders: { aggregate: { total: number } };
}

interface PerformanceAnalytics {
  protectedOrders: DatedValue[];
  claimsPaid: DatedValue[];
  protectionFees: { aggregate: { sum: { amount: number | string | null } } };
}

interface ProtectionData {
  analytics: OrderAnalytics;
  orders: OrderAnalytics;
  performance: PerformanceAnalytics;
}

const finiteNumber = (value: unknown) =>
  (typeof value === 'number' ||
    (typeof value === 'string' && value.trim() !== '')) &&
  Number.isFinite(Number(value));

const validRows = (rows: any, amount?: 'protectionTotal' | 'total') =>
  Array.isArray(rows) &&
  rows.every(
    (row) =>
      Number.isFinite(Date.parse(row?.createdAt)) &&
      (!amount || finiteNumber(row?.[amount])),
  );

const validOrders = (data: any): data is OrderAnalytics =>
  ['totalOrders', 'protectedOrders', 'unprotectedOrders'].every(
    (key) =>
      finiteNumber(data?.[key]?.aggregate?.total) &&
      Number(data[key].aggregate.total) >= 0,
  ) &&
  validRows(data?.totalOrders?.nodes) &&
  validRows(data?.protectedOrders?.nodes, 'protectionTotal');

const validPerformance = (data: any): data is PerformanceAnalytics => {
  const amount = data?.protectionFees?.aggregate?.sum?.amount;
  return (
    validRows(data?.protectedOrders, 'protectionTotal') &&
    validRows(data?.claimsPaid, 'total') &&
    (amount === null || finiteNumber(amount))
  );
};

function useProtectionData(
  { startDate, endDate }: ProtectionDates,
  channel = 'All',
) {
  const client = useApolloClient();
  const { storeId, storeProperties, storeLoading, refetchStoreProperties } =
    useStore();
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{
    scope: string;
    data?: ProtectionData;
    error?: boolean;
  }>();
  const scope = JSON.stringify([storeId, startDate, endDate, channel, retry]);
  const validRange =
    Number.isFinite(Date.parse(startDate)) &&
    Number.isFinite(Date.parse(endDate)) &&
    Date.parse(startDate) <= Date.parse(endDate);
  let currency = storeProperties?.currency || 'USD';
  try {
    getStoreCurrencyFormatter(currency, 0);
  } catch {
    currency = 'USD';
  }

  useEffect(() => {
    if (!storeId || !validRange) return;
    let active = true;
    const variables = { storeId, afterDate: startDate, beforeDate: endDate };
    const allOrders = client.query({
      query: GET_STORE_ANALYTICS,
      variables,
      fetchPolicy: 'network-only',
    });
    const performance = client.query({
      query: GET_DASHBOARD_ANALYTICS,
      variables: { storeId, startDate, endDate },
      fetchPolicy: 'network-only',
    });
    // Fees and issue costs in the existing channel operation are store-wide.
    // Keep financial results on the all-channel order denominator as well.
    const channelOrders =
      channel === 'All'
        ? allOrders
        : client.query({
            query: GET_STORE_ANALYTICS_BY_CHANNEL,
            variables: { ...variables, channelFilter: channel },
            fetchPolicy: 'network-only',
          });
    Promise.all([allOrders, performance, channelOrders])
      .then(([all, financial, selected]) => {
        if (
          all.errors?.length ||
          financial.errors?.length ||
          selected.errors?.length ||
          !validOrders(all.data) ||
          !validOrders(selected.data) ||
          !validPerformance(financial.data)
        ) {
          throw new Error('Incomplete Delivery Guarantee analytics');
        }
        if (active)
          setResult({
            scope,
            data: {
              analytics: all.data,
              orders: selected.data,
              performance: financial.data,
            },
          });
      })
      .catch(() => {
        if (active) setResult({ scope, error: true });
      });
    return () => {
      active = false;
    };
  }, [client, storeId, startDate, endDate, channel, scope, validRange]);

  const current = result?.scope === scope ? result : undefined;
  const hasCurrentStoreProperties = storeProperties?.id === storeId;
  const propertiesUnavailable =
    !!current?.data && !hasCurrentStoreProperties && storeLoading !== true;
  const error = current?.error || !validRange || propertiesUnavailable;
  return {
    data: hasCurrentStoreProperties ? current?.data : undefined,
    error,
    loading: !!storeId && !error && (!current || !hasCurrentStoreProperties),
    storeId,
    currency,
    retry: () => {
      if (!hasCurrentStoreProperties) {
        Promise.resolve()
          .then(() => refetchStoreProperties?.())
          .catch(() => {});
      }
      setRetry((value) => value + 1);
    },
  };
}

type ProtectionState = ReturnType<typeof useProtectionData>;

function DataStatus({ state }: { state: ProtectionState }) {
  if (!state.storeId)
    return (
      <p className="p-5 text-sm text-gray-500">
        Select a store to view Delivery Guarantee data.
      </p>
    );
  if (state.loading)
    return (
      <p role="status" className="p-5 text-sm text-gray-500">
        Loading Delivery Guarantee data…
      </p>
    );
  if (state.error)
    return (
      <div role="alert" className="space-y-3 p-5 text-sm text-red-700">
        <p>
          Delivery Guarantee data is unavailable. Try loading this date range
          again.
        </p>
        <Button onClick={state.retry} variant="outlined">
          Retry Delivery Guarantee data
        </Button>
      </div>
    );
  return null;
}

function MetricCards({
  data,
  currency,
}: {
  data: ProtectionData;
  currency: string;
}) {
  const summary = getDashboardMetricSummary(
    data.analytics as any,
    data.performance,
    currency,
  );
  const metrics = [
    [
      'Delivery Guarantee Revenue',
      summary.formattedProtectionRevenue,
      'Revenue from Delivery Guarantee fees',
    ],
    [
      'Attachment Rate',
      `${summary.attachmentRate}%`,
      `${summary.protectedOrders.toLocaleString()} of ${summary.totalOrders.toLocaleString()} orders guaranteed`,
    ],
    [
      'Net After Issue Cost',
      summary.formattedNetAfterIssueCost,
      'Revenue after paid issue resolutions',
    ],
    [
      'Avg. Delivery Guarantee / Order',
      summary.formattedAverageGuarantee,
      'Average fee per guaranteed order',
    ],
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {metrics.map(([label, value, description]) => (
        <div
          key={label}
          className="rounded-lg border border-gray-200 bg-white p-4"
        >
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-gray-900">
            {value}
          </p>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
        </div>
      ))}
    </div>
  );
}

const isEmpty = (data: ProtectionData) =>
  Number(data.analytics.totalOrders.aggregate.total) === 0 &&
  !data.performance.claimsPaid.length &&
  !Number(data.performance.protectionFees.aggregate.sum.amount);

export function ProtectionSummary({
  startDate,
  endDate,
  reportHref,
}: ProtectionDates & { reportHref?: string }) {
  const state = useProtectionData({ startDate, endDate });
  return (
    <section aria-label="Delivery Guarantee summary" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Delivery Guarantee
        </h2>
        {reportHref && (
          <Link
            className="text-sm font-medium text-blue-700 hover:underline"
            to={reportHref}
          >
            View full Delivery Guarantee report
          </Link>
        )}
      </div>
      <DataStatus state={state} />
      {state.data && (
        <>
          {isEmpty(state.data) && (
            <p className="text-sm text-gray-500">
              No Delivery Guarantee activity in this date range.
            </p>
          )}
          <MetricCards data={state.data} currency={state.currency} />
        </>
      )}
    </section>
  );
}

function chartRows(data: ProtectionData, startDate: string, endDate: string) {
  const start = Date.parse(startDate);
  const end = Date.parse(endDate);
  const day = 86400000;
  const days = Math.ceil((end - start + 1) / day);
  const interval = (days <= 7 ? 1 : days <= 31 ? 7 : 30) * day;
  const withTimestamp = (items: DatedValue[]) =>
    items.map((item) => ({ ...item, timestamp: Date.parse(item.createdAt) }));
  const totalOrderRows = withTimestamp(data.orders.totalOrders.nodes);
  const protectedOrderRows = withTimestamp(data.orders.protectedOrders.nodes);
  const revenueRows = withTimestamp(data.performance.protectedOrders);
  const issueRows = withTimestamp(data.performance.claimsPaid);
  const rows = [];
  const label = (time: number) =>
    new Date(time).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  for (let from = start; from <= end; from += interval) {
    const until = Math.min(from + interval, end + 1);
    const inRange = (item: { timestamp: number }) =>
      item.timestamp >= from && item.timestamp < until;
    const totalOrders = totalOrderRows.filter(inRange).length;
    const protectedOrders = protectedOrderRows.filter(inRange).length;
    rows.push({
      label: days <= 7 ? label(from) : `${label(from)} – ${label(until - 1)}`,
      totalOrders,
      protectedOrders,
      otherOrders: Math.max(0, totalOrders - protectedOrders),
      attachmentRate: totalOrders
        ? Math.round((protectedOrders / totalOrders) * 1000) / 10
        : 0,
      protectionRevenue: revenueRows
        .filter(inRange)
        .reduce((sum, item) => sum + Number(item.protectionTotal), 0),
      issueCost: issueRows
        .filter(inRange)
        .reduce((sum, item) => sum + Number(item.total), 0),
    });
  }
  return rows;
}

function ChartCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-5">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <p className="mb-5 mt-1 text-sm text-gray-500">{description}</p>
      {children}
    </section>
  );
}

export function ProtectionReport({ startDate, endDate }: ProtectionDates) {
  const [channel, setChannel] = useState('All');
  const state = useProtectionData({ startDate, endDate }, channel);
  const rows = useMemo(
    () => (state.data ? chartRows(state.data, startDate, endDate) : []),
    [state.data, startDate, endDate],
  );
  return (
    <section aria-label="Delivery Guarantee report" className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Delivery Guarantee Performance
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Financial results and summary metrics cover all channels.
        </p>
      </div>
      <DataStatus state={state} />
      {state.data && (
        <MetricCards data={state.data} currency={state.currency} />
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <label
          htmlFor="protection-order-channel"
          className="text-sm font-medium text-gray-700"
        >
          Order channel
        </label>
        <select
          id="protection-order-channel"
          className="rounded-md border-gray-300 text-sm"
          value={channel}
          onChange={(event) => setChannel(event.target.value)}
        >
          <option value="All">All channels</option>
          <option value="Online Store">Online Store</option>
          <option value="Shop">Shop</option>
        </select>
        <p className="text-xs text-gray-500">
          Filters attachment and order volume. Includes legacy orders without a
          channel.
        </p>
      </div>
      {state.data &&
        (isEmpty(state.data) ? (
          <p className="text-sm text-gray-500">
            No Delivery Guarantee activity in this date range.
          </p>
        ) : (
          <>
            {Number(state.data.orders.totalOrders.aggregate.total) === 0 && (
              <p className="text-sm text-gray-500">
                No orders for this channel in the selected date range.
              </p>
            )}
            <div className="grid gap-5 xl:grid-cols-2">
              <ChartCard
                title="Attachment Rate"
                description="Guaranteed order share over the selected range. Dates are UTC."
              >
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={rows}>
                    <CartesianGrid
                      stroke="#eef2f7"
                      strokeDasharray="4 6"
                      vertical={false}
                    />
                    <XAxis dataKey="label" />
                    <YAxis tickFormatter={(value) => `${value}%`} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Line
                      type="monotone"
                      dataKey="attachmentRate"
                      name="Attachment Rate"
                      stroke="#2563eb"
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard
                title="Order Volume"
                description="Guaranteed and not guaranteed orders over the selected range."
              >
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rows}>
                    <CartesianGrid
                      stroke="#eef2f7"
                      strokeDasharray="4 6"
                      vertical={false}
                    />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="protectedOrders"
                      name="Guaranteed"
                      stackId="orders"
                      fill="#2563eb"
                    />
                    <Bar
                      dataKey="otherOrders"
                      name="Not guaranteed"
                      stackId="orders"
                      fill="#cbd5e1"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <ChartCard
              title="Delivery Guarantee Revenue and Issue Cost"
              description="All channels. Delivery Guarantee values on orders compared with issue cost, grouped by order or issue creation date."
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={rows}>
                  <CartesianGrid
                    stroke="#eef2f7"
                    strokeDasharray="4 6"
                    vertical={false}
                  />
                  <XAxis dataKey="label" />
                  <YAxis
                    tickFormatter={(value) =>
                      getStoreCurrencyFormatter(state.currency, value)
                    }
                  />
                  <Tooltip
                    formatter={(value) =>
                      getStoreCurrencyFormatter(state.currency, Number(value))
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="protectionRevenue"
                    name="Delivery Guarantee Revenue"
                    fill="#2563eb"
                  />
                  <Bar dataKey="issueCost" name="Issue Cost" fill="#111827" />
                </BarChart>
              </ResponsiveContainer>
              <p className="mt-3 text-xs text-gray-500">
                Summary revenue uses recorded fees when available, with order
                values as a fallback. The chart uses order values excluding
                cancelled and replacement orders. Issue cost includes refund,
                reorder, and other resolutions.
              </p>
            </ChartCard>
          </>
        ))}
    </section>
  );
}
